/**
 * Management shell helpers — consultant / white-label firm context vs client reporting.
 * Org-type detection lets CYF admins see the same shell when they select a firm org.
 */

/**
 * @param {object|null|undefined} org
 * @returns {boolean}
 */
export const isConsultantFirmOrg = (org) => !!(org?.id && org.consultantId && org.id === org.consultantId);

/**
 * @param {object|null|undefined} org
 * @returns {boolean}
 */
export const isWhiteLabelFirmOrg = (org) =>
  !!(org?.id && org.whiteLabelId && org.whiteLabelId !== '0' && org.id === org.whiteLabelId);

/**
 * @param {object|null|undefined} org
 * @param {object|null|undefined} profile
 * @returns {boolean}
 */
export const isFirmOrg = (org, profile) => {
  if (!org?.id) return false;
  if (isConsultantFirmOrg(org) || isWhiteLabelFirmOrg(org)) return true;
  const consultantFirmId = resolveProfileFirmId(profile?.consultantAdmin);
  if (consultantFirmId && org.id === consultantFirmId) return true;
  const wlFirmId = resolveProfileFirmId(profile?.whiteLabelAdmin);
  if (wlFirmId && org.id === wlFirmId) return true;
  return false;
};

/**
 * Profile firm fields are often org id strings; treat bare booleans as unresolved.
 * @param {string|boolean|null|undefined} value
 * @returns {string|null}
 */
export const resolveProfileFirmId = (value) => {
  if (!value || value === true || value === 'true') return null;
  return String(value);
};

/**
 * @param {object|null|undefined} org
 * @param {object|null|undefined} profile
 * @returns {'consultant'|'whitelabel'|null}
 */
export const shellKind = (org, profile) => {
  if (!org?.id) return null;
  if (isConsultantFirmOrg(org)) return 'consultant';
  if (isWhiteLabelFirmOrg(org)) return 'whitelabel';
  const consultantFirmId = resolveProfileFirmId(profile?.consultantAdmin);
  if (consultantFirmId && org.id === consultantFirmId) return 'consultant';
  const wlFirmId = resolveProfileFirmId(profile?.whiteLabelAdmin);
  if (wlFirmId && org.id === wlFirmId) return 'whitelabel';
  return null;
};

/**
 * @param {object} state — Redux root
 * @returns {'consultant'|'whitelabel'|null}
 */
export const shellKindFromState = (state) =>
  shellKind(state?.organisation?.details, state?.profile?.details);

/**
 * @param {object} state
 * @returns {boolean}
 */
export const isManagementShell = (state) => shellKindFromState(state) != null;

/**
 * Firm id for back-to-firm / portfolio filtering.
 * @param {object|null|undefined} org
 * @param {object|null|undefined} profile
 * @returns {string|null}
 */
export const getFirmId = (org, profile) => {
  const kind = shellKind(org, profile);
  if (kind) return org.id;
  if (org?.consultantId && org.consultantId !== 'NONE' && org.id !== org.consultantId) {
    return org.consultantId;
  }
  if (org?.whiteLabelId && org.whiteLabelId !== '0' && org.id !== org.whiteLabelId) {
    return org.whiteLabelId;
  }
  return resolveProfileFirmId(profile?.consultantAdmin) || resolveProfileFirmId(profile?.whiteLabelAdmin);
};

/**
 * Consultant/WL (or CYF admin) viewing a client of a firm — not the firm itself.
 * @param {object} state
 * @returns {boolean}
 */
export const isClientWorkingContext = (state) => {
  const org = state?.organisation?.details;
  const profile = state?.profile?.details;
  if (!org?.id || shellKind(org, profile)) return false;

  const isConsultantUser = !!profile?.consultantAdmin;
  const isWlUser = !!profile?.whiteLabelAdmin;
  const isCyf = !!profile?.cyfAdmin;
  if (!isConsultantUser && !isWlUser && !isCyf) return false;

  const isConsultantClient =
    org.consultantId && org.consultantId !== 'NONE' && org.id !== org.consultantId;
  const isWlClient = org.whiteLabelId && org.whiteLabelId !== '0' && org.id !== org.whiteLabelId;

  if (isConsultantClient) {
    if (isCyf) return true;
    if (isConsultantUser) {
      const firmId = resolveProfileFirmId(profile.consultantAdmin);
      return !firmId || org.consultantId === firmId;
    }
  }
  if (isWlClient) {
    if (isCyf) return true;
    if (isWlUser) {
      const firmId = resolveProfileFirmId(profile.whiteLabelAdmin);
      return !firmId || org.whiteLabelId === firmId;
    }
  }
  return false;
};

/**
 * Group-of-companies parent: at least one other org lists this org as parentOrganisation.
 * @param {object|null|undefined} org
 * @param {object[]|null|undefined} orgList
 * @returns {boolean}
 */
export const isGroupParentOrg = (org, orgList) => {
  if (!org?.id || !Array.isArray(orgList)) return false;
  return orgList.some((x) => x?.parentOrganisation === org.id && x.id !== org.id);
};

/**
 * @param {object|null|undefined} org
 * @param {object[]|null|undefined} orgList
 * @returns {object[]}
 */
export const getGroupChildren = (org, orgList) => {
  if (!org?.id || !Array.isArray(orgList)) return [];
  return orgList.filter((x) => x?.parentOrganisation === org.id && x.id !== org.id);
};

/**
 * @param {'consultant'|'whitelabel'|null} kind
 * @returns {string}
 */
export const shellHomePath = (kind) => {
  if (kind === 'consultant') return '/consultant/dashboard';
  if (kind === 'whitelabel') return '/admin/organisations';
  return '/';
};
